// 🔧 content_bundle_model.js
// ✅ Purpose: Canonical runtime model for Content Engine bundles
// ✅ Features:
// - Represents one published content bundle
// - Optimized for public page reads (one Firestore read per area)
// - Built from published Content Items by the Bundle Builder
// - Supports future localization, markets, organizations, and profiles
// - Contains only runtime-ready content (no draft/editor metadata)

import {
  DEFAULT_SCOPE_ID,
} from "../constants/content_scopes";

import {
  DEFAULT_CONTENT_LOCALE,
} from "../constants/content_locales";

import {
  DEFAULT_CONTENT_PROFILE,
} from "../constants/content_profiles";

import {
  buildContentBundleKey,
} from "../helpers/content_bundle_key_helpers";

/**
 * Creates a canonical Content Bundle.
 *
 * Bundles are generated.
 * They are never manually edited.
 */
export function createContentBundle({
  area,

  scopeId = DEFAULT_SCOPE_ID,
  locale = DEFAULT_CONTENT_LOCALE,
  profile = DEFAULT_CONTENT_PROFILE,

  version = 1,

  generatedAt = null,
  generatedBy = null,

  content = {},

  metadata = {},
} = {}) {
  const bundleKey = buildContentBundleKey({
    scopeId,
    locale,
    profile,
    area,
  });

  return {
    bundleKey,

    area,

    scopeId,
    locale,
    profile,

    version,

    generatedAt,
    generatedBy,

    metadata,

    /**
     * Runtime content.
     *
     * Example:
     *
     * {
     *   hero: {
     *      title: "...",
     *      subtitle: "..."
     *   },
     *
     *   vote: {
     *      button: "Vote Now"
     *   }
     * }
     */
    content,
  };
}

/**
 * Returns a section from the bundle.
 *
 * Example:
 *
 * getBundleSection(bundle, "hero")
 */
export function getBundleSection(
  bundle = {},
  section = ""
) {
  return bundle.content?.[section] ?? {};
}

/**
 * Returns one content value.
 *
 * Example:
 *
 * getBundleValue(bundle, "hero", "title")
 */
export function getBundleValue(
  bundle = {},
  section = "",
  element = ""
) {
  return (
    bundle.content?.[section]?.[element] ?? null
  );
}

/**
 * Returns true when a section exists.
 */
export function hasBundleSection(
  bundle = {},
  section = ""
) {
  return Boolean(bundle.content?.[section]);
}

/**
 * Returns true when a value exists.
 */
export function hasBundleValue(
  bundle = {},
  section = "",
  element = ""
) {
  return (
    bundle.content?.[section]?.[element] !==
    undefined
  );
}

/**
 * Returns every section name.
 *
 * Useful for diagnostics and future admin tooling.
 */
export function getBundleSections(
  bundle = {}
) {
  return Object.keys(bundle.content ?? {});
}